import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose, setDisplayName } from 'recompact';

import { ANALYTICS_EVENTS, UNIFIED } from 'src/app/analytics/analyticsEvents';
import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { errorBoundary } from 'src/app/hocs';
import {
  checkoutRequiresAccount,
  redirectToOrderTracking,
  segmentAnalyticsEvent,
  segmentTrack,
  setShowCheckoutTransferModal,
} from 'src/app/state/actions';
import { accountLoadEverything, loadDeliveryAddress, patchAccount } from 'src/app/state/actions/account';
import {
  bffCheckoutCart,
  bffCreateCart,
  bffFetchCart,
  loadCartDeliveryMethods,
  loadCartDeliveryWindows,
  setPickupStationAsDeliveryMethod,
} from 'src/app/state/actions/cart';
import { setSelectedDeliveryMethodLabelFromCheckout } from 'src/app/state/actions/checkout';
import { clearCheckoutPickupStations, fetchCheckoutPickupStations } from 'src/app/state/actions/checkoutPickupStations';
import * as actionTypes from 'src/app/state/actions/types';
import {
  getAccountAgreedToTermsVersion,
  getAccountDefaultCardId,
  getAccountEmail,
  getAccountIDVerificationStatus,
  getBFFCart,
  getBFFCartDeliveryAddress,
  getBFFCartDeliveryMethod,
  getBFFCartDeliveryMethods,
  getBFFCartPaymentMethod,
  getCartItemQuantity,
  getCheckoutPatientMedicationIds,
  getCheckoutPickupStations,
  getCurrentAccountId,
  getCurrentCartId,
  getCurrentUser,
  getDeliveryAddresses,
  getHasSelectedCourierAsDeliveryMethod,
  getIsEligibleForPickups,
  getIsUserESIMailEligible,
  getOrderNeedsCreditCart,
  getOrderSubtotal,
  getPatients,
  getPaymentCards,
  getPrimaryPatient,
  getShowCheckoutTransferModal,
  getUserIsLoginSet,
  isDeliveryMethodsESIMailEligible,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { Cart, CartItem } from 'src/app/types';
import {
  CURRENT_CAPSULE_TOS_VERSION,
  DeliveryMethodType,
  ESI_MAIL_IDENTIFY_KEY,
  ID_UPLOAD_STATUS,
  REFRIGERATION_TYPES,
} from 'src/consts';
import type { DeliveryMethodLabels } from 'src/consts';

import Renderer from '.';

function makeMapStateToProps() {
  return (state: RootState) => {
    // Persistent state that holds user-selected values
    const checkout = state?.checkout;
    const cartId = getCurrentCartId(state);
    const patientMedicationIds = getCheckoutPatientMedicationIds(state);

    // Values that get loaded on mount
    const currentUser = getCurrentUser(state);
    const currentUserEmail = getAccountEmail(state);
    const accountId = getCurrentAccountId(state);
    const paymentCards = getPaymentCards(state);
    const bffCart = getBFFCart(state);
    const bffCartDeliveryMethods = getBFFCartDeliveryMethods(state);
    const bffCartDeliveryAddress = getBFFCartDeliveryAddress(state);
    const isDoorman = bffCartDeliveryAddress?.isDoorman;
    const bffCartDeliveryMethod = getBFFCartDeliveryMethod(state);
    const bffCartPaymentMethod = getBFFCartPaymentMethod(state);
    const isUserESIEligible = getIsUserESIMailEligible(state);
    const subtotal = getOrderSubtotal(state);
    const needsCreditCard = getOrderNeedsCreditCart(state);
    const showCheckoutTransferModal = getShowCheckoutTransferModal(state);
    const isEligibleForPickups = getIsEligibleForPickups(state);

    const prescriptionFulfillmentIds = bffCart?.prescriptionCartItems?.reduce((allIds, prescriptionCartItem) => {
      return allIds.concat(prescriptionCartItem.items.map(item => item.product.id));
    }, []);

    const {
      otcCartItems,
      preCreditTotal,
      tip,
      total,
      verifyId: idVerificationRequired,
      prescriptionCartItems,
    }: Cart = bffCart || {};
    const otcItemsLength = otcCartItems?.length;

    const allPatientsPrescriptionCartItems =
      prescriptionCartItems?.reduce((prev, curr) => [...prev, ...curr.items], [] as Array<CartItem>) ?? [];

    const cartItems = [...allPatientsPrescriptionCartItems, ...(otcCartItems || [])];

    // Credit Card
    // Check if one is selected || Fallback to default card, found on the Account
    const defaultCardId = getAccountDefaultCardId(state);
    const paymentCardId = bffCartPaymentMethod?.id || defaultCardId;

    // Delivery Window
    const deliveryWindow = checkout?.DeliveryWindow;

    const patients = getPatients(state);
    const primaryPatient = getPrimaryPatient(state);

    let allergyProfileComplete = false;
    if (primaryPatient) {
      allergyProfileComplete =
        primaryPatient.allergies !== null &&
        primaryPatient.medicalConditions !== null &&
        primaryPatient.otherMedications !== null;
    }

    const isAccountSet = getUserIsLoginSet(state);
    const idVerificationStatus = getAccountIDVerificationStatus(state);

    const prescriptionsThatNeedRefrigeration = prescriptionCartItems?.filter(
      prescriptionCartItem =>
        prescriptionCartItem?.items?.filter(
          item => item?.product?.refrigerationType !== REFRIGERATION_TYPES.no_refrigeration
        ).length
    );

    const isRefrigerationRequired = prescriptionsThatNeedRefrigeration?.length > 0;
    // this indicates that a setup was required at some point during this checkout
    // it won't change after the account setup is complete, but will change post checkout.
    const accountSetupRequired = state.checkout.requiresAccount;

    // this determines if the order is capable of being completed
    const orderCompletable =
      bffCart &&
      bffCartDeliveryAddress &&
      bffCartDeliveryMethod &&
      (!needsCreditCard || paymentCardId) &&
      isAccountSet &&
      (!idVerificationRequired ||
        (idVerificationStatus !== ID_UPLOAD_STATUS.unverified && idVerificationStatus !== ID_UPLOAD_STATUS.failed));

    const totalQuantity = getCartItemQuantity(state);

    const shouldShowDoormanText = bffCartDeliveryMethod
      ? bffCartDeliveryMethod?.key === DeliveryMethodType.courier && isDoorman
      : isDoorman;
    const instructions = bffCartDeliveryMethod
      ? bffCartDeliveryMethod?.key === DeliveryMethodType.courier
        ? bffCartDeliveryAddress?.instructions
        : ''
      : bffCartDeliveryAddress?.instructions;

    const isUserESIMailEligible = getIsUserESIMailEligible(state);
    const hasSelectedCourierAsDeliveryMethod = getHasSelectedCourierAsDeliveryMethod(state);
    const pickupStationsLength = getCheckoutPickupStations(state)?.length;

    const agreedToTermsVersion = getAccountAgreedToTermsVersion(state);

    const shouldShowTermsCallout = agreedToTermsVersion !== `${CURRENT_CAPSULE_TOS_VERSION}`;

    return {
      bffCartDeliveryMethods,
      bffCartDeliveryAddress,
      bffCartDeliveryMethod,
      hasSelectedCourierAsDeliveryMethod,
      bffCartPaymentMethod,
      isUserESIEligible,
      prescriptionFulfillmentIds,
      patientMedicationIds,
      pickupStationsLength,
      subtotal,
      deliveryWindow,
      paymentCardId,
      cartId,
      currentUser,
      currentUserEmail,
      accountId,
      cartItems,
      hasPaymentCards: !isEmpty(paymentCards),
      hasDeliveryAddresses: !isEmpty(getDeliveryAddresses(state)),
      hasMultiplePatients: patients?.length > 1,
      needsCreditCard,
      isAccountSet,
      isEligibleForPickups,
      isDoorman,
      isRefrigerationRequired,
      accountSetupRequired,
      idVerificationRequired,
      idVerificationStatus,
      orderCompletable,
      allergyProfileComplete,
      otcItemsLength,
      tip,
      total,
      preCreditTotal,
      totalQuantity,
      isUserESIMailEligible,
      shouldShowDoormanText,
      instructions,
      showCheckoutTransferModal,
      showPrice: bffCart?.showPrice ?? false,
      shouldShowTermsCallout,
    };
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchCreateCart: async accountId =>
    await dispatch(bffCreateCart()).then(async () => {
      const pickupAndDeliveryMethodsData = await Promise.all([
        dispatch(fetchCheckoutPickupStations()),
        //dispatch(loadCartDeliveryMethods()),
      ]);

      const { partnerName, pickupStations } = pickupAndDeliveryMethodsData[0];
      if (pickupStations?.length > 0) {
        dispatch({
          type: actionTypes.SEGMENT_IDENTIFY,
          accountId,
          traits: { corporate_pickup_eligible: true, corporate_pickup_partner: partnerName },
        });
      }

      const deliveryMethods = pickupAndDeliveryMethodsData[1];
      const hasESIMailAsAnOption = isDeliveryMethodsESIMailEligible(deliveryMethods);
      dispatch({
        type: actionTypes.SEGMENT_IDENTIFY,
        accountId,
        traits: { [ESI_MAIL_IDENTIFY_KEY]: hasESIMailAsAnOption ? 1 : 0 },
      });
    }),
  dispatchFetchCart: async cartId => await dispatch(bffFetchCart(cartId)),
  dispatchCheckoutCart: ({ cartId }) =>
    dispatch(
      bffCheckoutCart({
        cartId,
      })
    ),
  dispatchLoadAccount: () => dispatch(accountLoadEverything(false)),
  dispatchLoadDeliveryAddresses: () => dispatch(loadDeliveryAddress()),
  dispatchRequiresAccount: () => dispatch(checkoutRequiresAccount()),
  dispatchProcessOrderFailure: attributes =>
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.processOrderFailure, { ...attributes })),
  dispatchAddressListClicked: () => {
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.addressListClicked));
  },
  dispatchPaymentListClicked: () => {
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.paymentListClicked));
  },
  dispatchDeliveryWindowsListClicked: () => {
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.deliveryWindowsListClicked));
  },
  dispatchSignatureRequirementChecked: checked => {
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.signatureRequirementChecked + (checked ? 'Checked' : 'Unchecked')));
  },
  dispatchPlaceOrderButtonClicked: attributes => {
    dispatch(segmentAnalyticsEvent(ANALYTICS_EVENTS.placeOrderButtonClicked, { ...attributes }));
  },
  dispatchPatchTOS: accountId => {
    dispatch(patchAccount(accountId, { agreedToTermsVersion: CURRENT_CAPSULE_TOS_VERSION }));
  },
  dispatchLoadCartDeliveryWindows: async () => await dispatch(loadCartDeliveryWindows()),
  dispatchCloseCheckoutTransferModal: () => dispatch(setShowCheckoutTransferModal(false)),
  dispatchCompleteOrder: (orderId: string) => dispatch(redirectToOrderTracking(orderId)),
  dispatchGetPickupStations: () => dispatch(fetchCheckoutPickupStations()),
  dispatchClearPickupStations: () => dispatch(clearCheckoutPickupStations()),
  dispatchSetSelectedDeliveryMethodLabelFromCheckout: (deliveryMethodLabel: DeliveryMethodLabels) =>
    dispatch(setSelectedDeliveryMethodLabelFromCheckout(deliveryMethodLabel)),
  dispatchSetPickupStationAsDeliveryMethod: () => dispatch(setPickupStationAsDeliveryMethod()),
  dispatchTrackSubmitOrder: payload => dispatch(segmentTrack(UNIFIED.submitOrder, payload)),
  segmentTrack: (event: UnifiedEvent, payload?: object) => dispatch(segmentTrack(event, payload)),
  dispatchLoadCartDeliveryMethods: async (accountId: string) => {
    // Need to load delivery methods before pushing the next checkout state as product
    // wants us to skip the delivery method screen if ESI_MAIL is not an option
    const deliveryMethods = await dispatch(loadCartDeliveryMethods());
    const hasESIMailAsAnOption = isDeliveryMethodsESIMailEligible(deliveryMethods);
    dispatch({
      type: actionTypes.SEGMENT_IDENTIFY,
      accountId,
      traits: { [ESI_MAIL_IDENTIFY_KEY]: hasESIMailAsAnOption ? 1 : 0 },
    });
  },
});

const connector = connect(makeMapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(setDisplayName('Checkout'), errorBoundary, connector)(Renderer);
